'use client';

import DOMPurify from '@packages/config/utils/dompurify';
import {
  Accordion,
  AccordionDetails,
  AccordionItem,
  AccordionSummary,
} from '@packages/shared/src/ui/Accordion/Accordion';
import type { Language } from '@packages/config';
import config from '@packages/config';
import type { DeepPartial } from '@packages/config/src/default/types';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useNlSheetTexts } from './helpers/useNlSheetTexts';
import { subscribeSheetMessages } from './NewsletterSheet.messages';

const ChunkADomain = (domain: string) => (chunks: ReactNode) => (
  <a href={`https://${domain}`}>{chunks}</a>
);
const ChunkAVoucher =
  (
    newsletterSubscribe: Record<
      Language,
      {
        unsubscribeLink: string;
        voucherLink: string;
      }
    > &
      DeepPartial<
        Record<
          Language,
          {
            unsubscribeLink: string;
            voucherLink: string;
          }
        >
      >,
    configLanguage: Language,
  ) =>
  (chunks: ReactNode) => <a href={newsletterSubscribe[configLanguage].voucherLink}>{chunks}</a>;

export const TermsAccordion = () => {
  const intl = useIntl();
  const { language, defaultLanguage } = useI18n();
  const { nlTexts } = useNlSheetTexts();
  const {
    host: { domain },
    forms: { newsletterSubscribe },
  } = config;
  const configLanguage = language ?? defaultLanguage;

  return (
    <Accordion type="single" collapsible>
      <AccordionItem
        value="openTerms"
        className="bg-color-transparent before:bg-color-transparent border-none shadow-none [&>h3]:p-0"
      >
        <AccordionSummary
          aria-controls="newslettersheet-terms-content"
          id="newslettersheet-terms-title"
          className="text-size-body-sm justify-start border-none"
          icon="icon-emp-arrow-mini-down"
        >
          {(nlTexts && nlTexts.field_voucher_conditions_id?.label) ||
            intl.formatMessage(subscribeSheetMessages.termsHeadline)}
        </AccordionSummary>
        <AccordionDetails className="text-size-body-sm [&_a]:text-color-text-dark mt-1 mb-2 p-0">
          {nlTexts && nlTexts.field_voucher_conditions_id?.field_conditions?.processed ? (
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  nlTexts.field_voucher_conditions_id?.field_conditions.processed,
                ),
              }}
            />
          ) : (
            <FormattedMessage
              key="nl-sheet-terms-content"
              id={subscribeSheetMessages.termsContent.id}
              defaultMessage={subscribeSheetMessages.termsContent.defaultMessage}
              values={{
                aDomain: ChunkADomain(domain),
                aVoucher: ChunkAVoucher(newsletterSubscribe, configLanguage),
              }}
            />
          )}
        </AccordionDetails>
      </AccordionItem>
    </Accordion>
  );
};
